/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import { Headings } from '../Header/Header';
import HeaderWrapper from '../Header/HeaderWrapper';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import fundCodesTableStyles from './FundCodesTable.styles';

const dummyData = {
    productGroups: [
        {
            name: 'Global Neutral Balanced',
            fundData: [
                {
                    name: 'Manulife Balanced Portfolio GIF Select - InvestmentPlus',
                    mer: '2.72',
                    fundFeeRate: null,
                    fundCodes: {
                        fe: 'MGF2840',
                        be: 'MGF2839',
                        ll: 'MGF2841',
                        nl: 'MGF7525',
                        fcl: null,
                        psf: null,
                    },
                    fundStatus: 'Open',
                },
                {
                    name: 'Manulife Balanced Portfolio GIF Select - InvestmentPlus (Elite)',
                    mer: '8.45',
                    fundFeeRate: null,
                    fundCodes: {
                        fe: 'MGF2840',
                        be: 'MGF2839',
                        ll: 'MGF2841',
                        nl: null,
                        fcl: null,
                        psf: null,
                    },
                    fundStatus: 'ADO',
                },
                {
                    name: 'Manulife Balanced Portfolio GIF Select - InvestmentPlus (F-Class)',
                    mer: '1.12',
                    fundFeeRate: null,
                    fundStatus: 'Open',
                    fundCodes: {
                        fe: 'MGF2840',
                        be: null,
                        ll: 'MGF2841',
                        nl: 'MGF7525',
                        fcl: null,
                        psf: null,
                    },
                },
            ],
        },
        {
            name: 'Canadian Fixed income',
            fundData: [
                {
                    name: 'Manulife Balanced Portfolio GIF Select (original) - 75 Series',
                    mer: '2.37',
                    fundFeeRate: null,
                    fundCodes: {
                        fe: 'MGF2840',
                        be: 'MGH4654',
                        ll: null,
                        nl: 'MGF7525',
                        fcl: null,
                        psf: null,
                    },
                    fundStatus: 'Open',
                },
            ],
        },
    ],
};

export interface FundCodesTableColumn {
    label?: string;
    value?: string;
    hasChildColumns?: boolean;
    childColumns: {
        label?: string;
        value?: string;
    }[];
}

export interface FundCodesTableOptions {
    id: string;
    titles: Headings[];
    columns?: FundCodesTableColumn[];
    productGroup?: {
        dataKey: string;
        groupDataKey: string;
        fundDataKey: string;
    };
    rows?: {
        rowsDataKey: string;
        rowGroupDataKey: string;
    };
    rowGroup?: string;
    transformRowsData?: boolean;
    functionToTransformRows?: string;
    typographyClass?: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, FundCodesTableOptions {}

interface Widgetprops extends commonTypes.WidgetInstanceProps {}

const FundCodesTable = (props: Widgetprops) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { styles, columns, productGroup, ...rest } = widgetProperties;
    const i18n = commonHooks.useLocalization();
    const annotator = commonHooks.useAnnotator();
    const { data: _data } = props;
    // TODO: remove dummyData once we have data for fund codes table
    const data = { ..._data, ...dummyData };
    const productGroups = JsonQuery(productGroup?.dataKey as string, { data }).value;
    const rowsData =
        productGroups?.length > 0 &&
        productGroups?.map((pg: any) => ({
            group: JsonQuery(productGroup?.groupDataKey as string, { data: pg }).value,
            fundData: JsonQuery(productGroup?.fundDataKey as string, { data: pg }).value,
        }));

    let transformedData = {
        title: true,
        transformedRowsData: rowsData,
        transformedData: data,
        columns,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        if (rest.transformRowsData) {
            // eslint-disable-next-line no-new-func
            const _transformedData = commonUtils.transformDataWithGlobals(
                transformFunctionName,
                rest.functionToTransformRows,
                {
                    rowsData,
                    data,
                    annotator,
                    ...transformedData,
                }
            );

            if (_transformedData) {
                transformedData = { ...transformedData, ..._transformedData };
            }
        }
    } catch (e) {
        console.log(e);
    }

    const childColumns = transformedData?.columns?.filter(
        (c: FundCodesTableColumn) => c.hasChildColumns
    );
    const filteredColLen = childColumns?.length || 0;
    const totalCols = transformedData?.columns?.reduce((acc, c) => {
        if (c.childColumns) {
            return acc + c.childColumns.length;
        } else {
            return acc + 1;
        }
    }, 0);

    return (
        <>
            <div
                className={
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }
                css={css`
                    ${fundCodesTableStyles}${getTextSchemaProperties(
                        widgetProperties,
                        props?.data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles || ''}
                `}
            >
                {transformedData.title ? (
                    <div className="fund-codes-table-title">
                        <HeaderWrapper headings={rest?.titles} data={data} />
                    </div>
                ) : (
                    ''
                )}
                <table id={rest.id} className={'fund-codes-table'}>
                    {transformedData?.columns?.filter((c) => !!c.label)?.length ? (
                        <thead>
                            <tr>
                                {transformedData?.columns?.map(
                                    (c: FundCodesTableColumn, index: number) => {
                                        const anyHasChildCol = filteredColLen > 0;

                                        return !c.hasChildColumns ? (
                                            <th
                                                key={index}
                                                rowSpan={anyHasChildCol ? 2 : 0}
                                                dangerouslySetInnerHTML={{
                                                    __html: i18n.format(c.label || '', {
                                                        ...data,
                                                    }),
                                                }}
                                            />
                                        ) : (
                                            <th
                                                key={index}
                                                colSpan={
                                                    c.hasChildColumns ? c?.childColumns?.length : 0
                                                }
                                                dangerouslySetInnerHTML={{
                                                    __html: i18n.format(c.label || '', {
                                                        ...data,
                                                    }),
                                                }}
                                            />
                                        );
                                    }
                                )}
                            </tr>
                            {filteredColLen > 0 && (
                                <tr>
                                    {childColumns?.map((col: FundCodesTableColumn) => (
                                        <>
                                            {col?.childColumns?.map((ch: any) => (
                                                <th
                                                    className="child-columns-header"
                                                    key={ch.label}
                                                    dangerouslySetInnerHTML={{
                                                        __html: i18n.format(ch.label || '', {
                                                            ...data,
                                                        }),
                                                    }}
                                                />
                                            ))}
                                        </>
                                    ))}
                                </tr>
                            )}
                        </thead>
                    ) : null}

                    <tbody>
                        {transformedData?.transformedRowsData?.length
                            ? transformedData?.transformedRowsData?.map((r: any, index: number) => {
                                  return (
                                      <>
                                          {Object.keys(r)?.map((rKey: string) => (
                                              <>
                                                  {rKey === 'group' ? (
                                                      <tr
                                                          key={`group-${index}`}
                                                          className={'group-row'}
                                                      >
                                                          <td colSpan={totalCols}>{r.group}</td>
                                                      </tr>
                                                  ) : (
                                                      <>
                                                          {r?.fundData?.length > 0
                                                              ? r?.fundData?.map((fd: any) => (
                                                                    <tr
                                                                        key={index}
                                                                        className={`fund-data-row ${fd.cssClass}`}
                                                                    >
                                                                        {transformedData?.columns?.map(
                                                                            (
                                                                                c: FundCodesTableColumn,
                                                                                id: number
                                                                            ) => {
                                                                                return (
                                                                                    <>
                                                                                        {!c.hasChildColumns ? (
                                                                                            <td
                                                                                                className={
                                                                                                    'table-cell'
                                                                                                }
                                                                                                key={`${c.value}${index}${id}`}
                                                                                                dangerouslySetInnerHTML={{
                                                                                                    __html:
                                                                                                        i18n.format(
                                                                                                            c.value,
                                                                                                            {
                                                                                                                ...fd,
                                                                                                            }
                                                                                                        ) ||
                                                                                                        '—',
                                                                                                }}
                                                                                            />
                                                                                        ) : (
                                                                                            <>
                                                                                                {c?.childColumns?.map(
                                                                                                    (
                                                                                                        cc: any
                                                                                                    ) => {
                                                                                                        return (
                                                                                                            <td
                                                                                                                className={
                                                                                                                    'table-cell'
                                                                                                                }
                                                                                                                key={`${c.value}${index}${id}`}
                                                                                                                dangerouslySetInnerHTML={{
                                                                                                                    __html:
                                                                                                                        i18n.format(
                                                                                                                            cc.value,
                                                                                                                            {
                                                                                                                                ...fd,
                                                                                                                            }
                                                                                                                        ) ||
                                                                                                                        '—',
                                                                                                                }}
                                                                                                            />
                                                                                                        );
                                                                                                    }
                                                                                                )}
                                                                                            </>
                                                                                        )}
                                                                                    </>
                                                                                );
                                                                            }
                                                                        )}
                                                                    </tr>
                                                                ))
                                                              : null}
                                                      </>
                                                  )}
                                              </>
                                          ))}
                                      </>
                                  );
                              })
                            : null}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default FundCodesTable;
